import { useQueryClient } from '@tanstack/react-query';
import { useReactTable } from '@tanstack/react-table';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, PaginationState
} from '@tanstack/table-core';
import clsx from 'clsx';
import React, {
  Suspense, useCallback, useMemo, useRef, useState
} from 'react';
import { BsArrowRightCircle } from 'react-icons/bs';
import { FaRegEdit } from 'react-icons/fa';
import { FaCircleCheck } from 'react-icons/fa6';
import { FcProcess } from 'react-icons/fc';
import { FiMoreVertical, FiPlus } from 'react-icons/fi';
import { HiTrash } from 'react-icons/hi';
import { IoCloseCircleSharp } from 'react-icons/io5';
import { PiClockCountdownBold } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Button } from '@/components/button/button';
import { DeleteModal } from '@/components/deleteModal/deleteModal';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import styles from '@/components/table/table.module.scss';
import { Tooltip } from '@/components/tooltip/tooltip';
import { Heading } from '@/components/typography/heading';
import {
  dataContractsKeys, useContract, useContracts, useDeleteContract
} from '@/hooks/queries/useDataContracts';
import { useAuthContext } from '@/hooks/useAuthContext';
import { Contract, ContractStatus } from '@/types/dataContracts';

import { ActionMenu, ActionMenuItem } from './ActionMenu';
import dataCatalogStyles from './dataCatalog.module.scss';
import UpdateContractModal from './details/UpdateContractModal';

const columnHelper = createColumnHelper<Contract>();

const statusTooltips: Record<ContractStatus, React.ReactNode> = {
  [ContractStatus.DRAFT]: 'Contract is in draft state.',
  [ContractStatus.QUEUED]: 'Waiting to be processed.',
  [ContractStatus.PROCESSING]: 'Processing is in progress.',
  [ContractStatus.FAILED]: (
    <div>
      <div>Processing failed. Check error details for information and resolution tips.</div>
      <div className={ dataCatalogStyles.tooltipLearnMore }>
        <span>Learn more</span>
        <BsArrowRightCircle size={ 14 } />
      </div>
    </div>
  ),
  [ContractStatus.COMPLETED]: 'Processing completed successfully.',
  [ContractStatus.DELETING]: 'Contract is being deleted.'
};

const statusConfig: Record<ContractStatus, { icon: React.ReactNode; label: string }> = {
  [ContractStatus.DRAFT]: {
    icon: <PiClockCountdownBold className={ dataCatalogStyles.queuedIcon } />,
    label: 'Draft'
  },
  [ContractStatus.QUEUED]: {
    icon: <PiClockCountdownBold className={ dataCatalogStyles.queuedIcon } />,
    label: 'Queued'
  },
  [ContractStatus.PROCESSING]: {
    icon: <FcProcess className={ dataCatalogStyles.processingIcon } />,
    label: 'Processing'
  },
  [ContractStatus.FAILED]: {
    icon: <IoCloseCircleSharp className={ dataCatalogStyles.failedIcon } />,
    label: 'Failed'
  },
  [ContractStatus.COMPLETED]: {
    icon: <FaCircleCheck className={ dataCatalogStyles.completedIcon } />,
    label: 'Completed'
  },
  [ContractStatus.DELETING]: {
    icon: <HiTrash className={ dataCatalogStyles.deletingIcon } />,
    label: 'Deleting'
  }
};

function DataCatalogPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useContracts();
  const { sessionContext, canAccess } = useAuthContext();
  const deleteMutation = useDeleteContract();
  const [ filterText, setFilterText ] = useState<string>( '' );
  const [ pagination, setPagination ] = useState<PaginationState>( {
    pageIndex: 0,
    pageSize: 10
  } );
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState( false );
  const [ deletingContract, setDeletingContract ] = useState<{ id: string; name: string } | null>( null );
  const [ showUpdateModal, setShowUpdateModal ] = useState( false );
  const [ updatingContractId, setUpdatingContractId ] = useState<string | null>( null );
  const deletePollingRef = useRef<ReturnType<typeof setInterval>>( undefined );

  const { data: updatingContract } = useContract( showUpdateModal ? updatingContractId : null );

  const contracts = data?.contracts ?? [];

  const handleUpdateContract = useCallback( ( contractId: string ) => {
    setUpdatingContractId( contractId );
    setShowUpdateModal( true );
  }, [] );

  const handleOpenDeleteModal = useCallback( ( contractId: string, contractName: string ) => {
    setDeletingContract( { id: contractId, name: contractName } );
    setIsDeleteModalOpen( true );
  }, [] );

  const handleCloseDeleteModal = useCallback( () => {
    setIsDeleteModalOpen( false );
    setDeletingContract( null );
  }, [] );

  const handleConfirmDelete = useCallback( async() => {
    if ( !deletingContract ) return;
    await deleteMutation.mutateAsync( deletingContract.id );
    toast.success( 'Contract deletion started' );
    if ( deletePollingRef.current ) clearInterval( deletePollingRef.current );
    let attempts = 0;
    deletePollingRef.current = setInterval( () => {
      attempts++;
      queryClient.invalidateQueries( { queryKey: dataContractsKeys.list } );
      if ( attempts >= 15 ) clearInterval( deletePollingRef.current );
    }, 3000 );
    handleCloseDeleteModal();
  }, [ deletingContract, deleteMutation, queryClient, handleCloseDeleteModal ] );

  const handleRowClick = useCallback( ( contract: Contract ) => {
    navigate( `/dataCatalog/details?${ new URLSearchParams( { id: contract.contract_id } ) }` );
  }, [ navigate ] );

  const columns = useMemo( () => [
    columnHelper.accessor( 'name', {
      header: 'Name',
      cell: info => info.getValue(),
      meta: {
        classStr: buildColumnClassString( { width: 25, styles: ColumnModEnums.NO_OVERFLOW } ),
        width: 25
      }
    } ),
    columnHelper.accessor( 'status', {
      header: 'Processing Status',
      cell: info => {
        const status = info.getValue();
        const config = statusConfig[status] ?? statusConfig[ContractStatus.DRAFT];
        const tooltip = statusTooltips[status] ?? statusTooltips[ContractStatus.DRAFT];

        return (
          <Tooltip content={ tooltip } position="top">
            <div className={ dataCatalogStyles.statusContainer }>
              { config.icon }
              <span className={ dataCatalogStyles.statusLabel }>
                { config.label }
              </span>
            </div>
          </Tooltip>
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 15, styles: ColumnModEnums.CENTER } ),
        width: 15
      }
    } ),
    columnHelper.accessor( 'updated_at', {
      header: 'Last Updated',
      cell: info => {
        const val = info.getValue();
        if ( !val ) return '-';
        return new Date( val ).toLocaleString();
      },
      meta: {
        classStr: buildColumnClassString( { width: 20, styles: ColumnModEnums.NO_OVERFLOW } ),
        width: 20
      }
    } ),
    columnHelper.accessor( 'source_type', {
      header: 'Source',
      cell: info => info.getValue() || '-',
      meta: {
        classStr: buildColumnClassString( { width: 10, styles: ColumnModEnums.CENTER } ),
        width: 10
      }
    } ),
    columnHelper.accessor( 'owner', {
      header: 'Owner',
      cell: info => info.getValue() || '-',
      meta: {
        classStr: buildColumnClassString( { width: 20, styles: ColumnModEnums.NO_OVERFLOW } ),
        width: 20
      }
    } ),
    columnHelper.display( {
      id: 'actions',
      header: 'Actions',
      cell: info => {
        const contract = info.row.original;
        const canEdit = canAccess( 'can_manage_users' ) || contract.submitted_by === sessionContext?.userId;

        const actionIcon = (
          <span>
            <Icon icon={ FiMoreVertical } size="md" />
          </span>
        );

        return (
          <ActionMenu icon={ actionIcon } disabled={ !canEdit }>
            <ActionMenuItem
              value="Update Contract"
              icon={ <FaRegEdit size={ 16 } /> }
              onClick={ () => handleUpdateContract( contract.contract_id ) }
            />
            <ActionMenuItem
              value="Delete Contract"
              icon={ <HiTrash size={ 16 } /> }
              onClick={ () => handleOpenDeleteModal( contract.contract_id, contract.name ) }
            />
          </ActionMenu>
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 10, styles: ColumnModEnums.CENTER } ),
        width: 10
      }
    } )
  ], [ handleUpdateContract, handleOpenDeleteModal, sessionContext, canAccess ] );

  const filteredContracts = useMemo( () => {
    if ( !filterText ) return contracts;
    return contracts.filter( contract => contract.name.toLowerCase().includes( filterText.toLowerCase() )
    );
  }, [ contracts, filterText ] );

  const table = useReactTable<Contract>( {
    data: filteredContracts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination
    },
    enableSorting: false
  } );

  return (
    <div className="page">
      <ToastContainer />

      <DeleteModal
        isOpen={ isDeleteModalOpen }
        onClose={ handleCloseDeleteModal }
        onDelete={ handleConfirmDelete }
        entityName={ deletingContract?.name || '' }
        entityType="contract"
      />

      { showUpdateModal && updatingContract && (
        <UpdateContractModal
          contractId={ updatingContract.contract_id }
          contractName={ updatingContract.name }
          currentYaml={ updatingContract.yaml_content || '' }
          onClose={ () => { setShowUpdateModal( false ); setUpdatingContractId( null ); } }
        />
      ) }
      <div className={ clsx( 'layout__row', 'pad', 'layout__info' ) }>
        <span className="info_name">
          <Heading as="h2">Data Contracts <span style={ { color: '#0040ff', fontSize: '14px', fontWeight: 700, letterSpacing: '0.5px', verticalAlign: 'middle' } }>BETA</span></Heading>
        </span>
        <Button variant='primary' icon={ <Icon icon={ FiPlus } /> } asLink href='/dataCatalog/create'>
          Create Data Contract
        </Button>
      </div>

      <div className={ clsx( 'layout__row', 'pad' ) }>
        <Input
          type='text'
          label='Filter by name'
          placeholder='Filter by name...'
          value={ filterText }
          onChange={ ( e ) => setFilterText( e.target.value ) }
          data-testid='datacatalog-filter-input'
          className='page_filter_input'
        />
      </div>

      { isLoading ? (
        <Loader />
      ) : (
        <>
          <div className={ styles.table }>
            <NativeTable table={ table } onRowClick={ handleRowClick } />
          </div>
        </>
      ) }
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <DataCatalogPage />
    </Suspense>
  );
}
